import React from 'react';
import { Calendar, AlertCircle, Car, Shield, Info } from 'lucide-react';
import { useFormat } from '../../../../../i18n/format.js';

/**
 * Exit reasons with French labels and descriptions
 */
const EXIT_REASONS = [
  { value: 'sale', label: 'Vente', description: 'Vehicule vendu a un tiers', financialField: 'sale_price_eur' },
  { value: 'lease_end', label: 'Fin de Leasing', description: 'Fin de contrat LLD/LCD/LOA', financialField: 'leasing_penalty_amount_eur' },
  { value: 'claim_theft', label: 'Sinistre - Vol', description: 'Vol du vehicule', financialField: 'indemnity_amount_eur' },
  { value: 'claim_accident', label: 'Sinistre - Accident', description: 'Perte totale suite accident', financialField: 'indemnity_amount_eur' },
  { value: 'claim_other', label: 'Sinistre - Autre', description: 'Autre type de sinistre', financialField: 'indemnity_amount_eur' },
  { value: 'breakdown', label: 'Panne irreparable', description: 'Vehicule irreparable, mise a la casse', financialField: 'scrapyard_price_eur' },
  { value: 'seizure', label: 'Saisie judiciaire', description: 'Vehicule saisi par autorites', financialField: 'accounting_loss_eur' },
  { value: 'other', label: 'Autre', description: 'Autre motif de sortie', financialField: 'other_amount_eur' },
];

/**
 * Insurance modes for exit workflow
 */
const INSURANCE_MODES = [
  {
    value: 'fleet',
    label: 'Contrat Flotte',
    description: 'Le vehicule sera retire du contrat flotte existant (pas de resiliation individuelle)',
  },
  {
    value: 'individual',
    label: 'Contrat Individuel',
    description: 'Le contrat individuel sera resilie (demande de releve d\'informations)',
  },
];

/**
 * StepExitContext - First step of vehicle exit workflow
 * Captures: exit reason, effective date, insurance mode, optional notes
 */
function StepExitContext({ formData, updateFormData, vehicle }) {
  const { fmt } = useFormat();
  const selectedReason = EXIT_REASONS.find(r => r.value === formData.reason);

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Contexte de Sortie</h3>

      {/* Vehicle Info Banner */}
      {vehicle && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <Car className="w-8 h-8 text-gray-400" />
            <div>
              <p className="font-semibold text-gray-900">
                {vehicle.make} {vehicle.model} ({vehicle.year})
              </p>
              <p className="text-sm text-gray-600">{vehicle.license_plate}</p>
            </div>
          </div>
        </div>
      )}

      {/* Exit Reason Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Motif de sortie <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {EXIT_REASONS.map(reason => (
            <button
              key={reason.value}
              type="button"
              onClick={() => updateFormData('reason', reason.value)}
              className={`p-3 border-2 rounded-lg text-left transition-all ${
                formData.reason === reason.value
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50'
              }`}
            >
              <span className="font-medium text-sm text-gray-900">{reason.label}</span>
              <p className="text-xs text-gray-500 mt-1">{reason.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Other Nature Text (if reason is 'other') */}
      {formData.reason === 'other' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preciser le motif <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.other_nature_text || ''}
            onChange={(e) => updateFormData('other_nature_text', e.target.value)}
            placeholder="Ex: Donation, Destruction volontaire..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
      )}

      {/* Linked Claim Warning */}
      {(formData.reason === 'claim_theft' || formData.reason === 'claim_accident' || formData.reason === 'claim_other') && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900">Sinistre declare</h4>
              <p className="text-sm text-amber-700 mt-1">
                Assurez-vous que le sinistre est bien declare dans le module Sinistres avant de proceder a la sortie.
              </p>
              {formData.claim_id && (
                <p className="text-sm text-amber-800 mt-2 font-medium">
                  Sinistre lie: #{formData.claim_id}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Exit Effective Date */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date effective de sortie <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Calendar className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={formData.exit_effective_date || ''}
              onChange={(e) => updateFormData('exit_effective_date', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Date de la transaction (vente, fin de contrat, etc.)</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date d'enregistrement
          </label>
          <div className="relative">
            <Calendar className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="date"
              value={formData.recorded_at || new Date().toISOString().split('T')[0]}
              onChange={(e) => updateFormData('recorded_at', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-gray-50"
              readOnly
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Date de saisie dans le systeme</p>
        </div>
      </div>

      {/* Insurance Mode */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Mode d'assurance
          </div>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {INSURANCE_MODES.map(mode => (
            <button
              key={mode.value}
              type="button"
              onClick={() => updateFormData('insurance_mode', mode.value)}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                formData.insurance_mode === mode.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
              }`}
            >
              <span className="font-medium text-gray-900">{mode.label}</span>
              <p className="text-sm text-gray-600 mt-1">{mode.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* J+10 SLA Info */}
      {formData.exit_effective_date && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900">Suivi Assurance J+10</h4>
              <p className="text-sm text-blue-700 mt-1">
                Date limite de relance assureur: {' '}
                <span className="font-semibold">
                  {fmt.date(new Date(new Date(formData.exit_effective_date).getTime() + 10 * 24 * 60 * 60 * 1000))}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StepExitContext;
export { EXIT_REASONS, INSURANCE_MODES };
